import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { getMenus, updateCampaign, duplicateMenu } from '../../services/api';
import MenuChecklist from './MenuChecklist';

// Add existing, unassigned menus to a campaign.
export function AddMenusDialog({ open, onOpenChange, campaign, onSaved }) {
  const [menus, setMenus] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState({});
  const [saving, setSaving] = useState(false);
  const channelId = campaign?.channelId;

  useEffect(() => {
    if (!open || !channelId) return undefined;
    let cancelled = false;
    setSelected({});
    setLoading(true);
    getMenus(channelId)
      .then((data) => {
        if (cancelled) return;
        const members = new Set(campaign.menuIds || []);
        setMenus((data.menus || []).filter((m) => m.status !== 'replaced' && !m.campaignId && !members.has(m.id)));
      })
      .catch(() => { if (!cancelled) toast.error('Failed to load menus'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, channelId]);

  const ids = menus.filter((m) => selected[m.id]).map((m) => m.id);

  const save = async () => {
    setSaving(true);
    try {
      await updateCampaign(campaign.id, { menuIds: [...(campaign.menuIds || []), ...ids] });
      onOpenChange(false);
      onSaved();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add menus');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#18181B] border-[#27272A] max-w-lg">
        <DialogHeader>
          <DialogTitle>Add menus</DialogTitle>
          <DialogDescription className="text-[#A1A1AA]">Choose menus to add to this campaign.</DialogDescription>
        </DialogHeader>
        <MenuChecklist
          menus={menus}
          loading={loading}
          selected={selected}
          onToggle={(id) => setSelected((s) => ({ ...s, [id]: !s[id] }))}
          emptyText="No available menus to add."
          testIdPrefix="add-menu"
        />
        <DialogFooter>
          <Button variant="outline" className="border-[#27272A]" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button className="btn-primary" onClick={save} disabled={!ids.length || saving} data-testid="add-menus-confirm">
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Add{ids.length ? ` (${ids.length})` : ''}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Copy a published menu (keeping its alias) into the campaign so it can be edited, then launched in its place.
export function ReplaceLiveDialog({ open, onOpenChange, campaign, onSaved }) {
  const [menus, setMenus] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const channelId = campaign?.channelId;

  useEffect(() => {
    if (!open || !channelId) return undefined;
    let cancelled = false;
    setLoading(true);
    getMenus(channelId)
      .then((data) => {
        if (cancelled) return;
        const memberIds = new Set(campaign.menuIds || []);
        const all = data.menus || [];
        const heldAliases = new Set(all.filter((m) => memberIds.has(m.id) && m.richMenuAliasId).map((m) => m.richMenuAliasId));
        setMenus(all.filter((m) => m.status === 'published' && m.richMenuAliasId && !m.campaignId
          && !memberIds.has(m.id) && !heldAliases.has(m.richMenuAliasId)));
      })
      .catch(() => { if (!cancelled) toast.error('Failed to load menus'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, channelId]);

  const choose = async (menu) => {
    setBusyId(menu.id);
    try {
      await duplicateMenu(menu.id, null, { keepAlias: true, campaignId: campaign.id });
      toast.success(`Copy created with alias ${menu.richMenuAliasId} — edit it, then launch`);
      onOpenChange(false);
      onSaved();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create copy');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#18181B] border-[#27272A] max-w-lg">
        <DialogHeader>
          <DialogTitle>Replace a live menu</DialogTitle>
          <DialogDescription className="text-[#A1A1AA]">
            Pick a published menu. A copy that keeps its alias is added to this campaign; the live menu stays untouched until you launch.
          </DialogDescription>
        </DialogHeader>
        <div className="max-h-64 overflow-y-auto border border-[#27272A] rounded-lg divide-y divide-[#27272A]">
          {loading ? (
            <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-[#52525B]" /></div>
          ) : menus.length === 0 ? (
            <p className="text-sm text-[#A1A1AA] p-4 text-center">No published menus with an alias to replace.</p>
          ) : menus.map((m) => (
            <div key={m.id} className="flex items-center gap-3 px-3 py-2.5" data-testid={`replace-menu-${m.id}`}>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{m.name}</p>
                <p className="text-xs font-mono text-[#A1A1AA] truncate">{m.richMenuAliasId}</p>
              </div>
              <Button size="sm" variant="outline" className="border-[#27272A]" disabled={!!busyId} onClick={() => choose(m)}>
                {busyId === m.id && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}
                Replace
              </Button>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default AddMenusDialog;
